/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { Plus } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState, type ChangeEvent, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { Button } from '../../components/ui/button.js';
import { Modal } from '../../components/ui/modal.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { Pagination } from '../../components/ui/pagination.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type Therapist, type TherapistsPageParams } from '../../lib/api/resources.js';
import { useTherapistsPage } from '../../lib/queries/clinical.js';
import { uiStore } from '../../lib/stores/ui-store.js';
import { cn } from '../../lib/utils/cn.js';

import { TherapistForm } from './therapist-form.js';
import { TherapistProfile } from './therapist-profile.js';
import { TherapistTable } from './therapist-table.js';

import type { TherapistCategory } from '@therapy/shared-validators';

type CategoryFilter = 'all' | TherapistCategory;
const CATEGORIES: readonly CategoryFilter[] = ['all', 'on_roll', 'consultant'];
const PAGE_LIMIT = 20;

/** Build the server query params from the page's filter state. */
function pageParams(offset: number, q: string, category: CategoryFilter): TherapistsPageParams {
  return {
    limit: PAGE_LIMIT,
    offset,
    ...(q !== '' ? { q } : {}),
    ...(category !== 'all' ? { category } : {}),
  };
}

const SearchBar = observer(function SearchBar(): JSX.Element {
  const { t } = useTranslation();
  const onSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    uiStore.setSearch(e.target.value);
  }, []);
  return (
    <input
      type="search"
      value={uiStore.search}
      onChange={onSearch}
      placeholder={t('admin.therapists.searchPlaceholder')}
      aria-label={t('admin.therapists.searchPlaceholder')}
      className="focus-ring mb-4 h-11 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
    />
  );
});

function CategoryTab({
  value,
  active,
  onCategory,
}: {
  readonly value: CategoryFilter;
  readonly active: boolean;
  readonly onCategory: (value: CategoryFilter) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const onClick = useCallback(() => {
    onCategory(value);
  }, [onCategory, value]);
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'focus-ring rounded-full px-4 py-1.5 text-sm font-medium',
        active
          ? 'bg-[var(--accent)] text-white'
          : 'border border-slate-200 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300',
      )}
    >
      {value === 'all'
        ? t('admin.therapists.tab.all')
        : t(`form.therapist.categoryOption.${value}`)}
    </button>
  );
}

function CategoryTabs({
  category,
  onCategory,
}: {
  readonly category: CategoryFilter;
  readonly onCategory: (value: CategoryFilter) => void;
}): JSX.Element {
  return (
    <div className="mb-4 flex gap-2" role="tablist">
      {CATEGORIES.map((value) => (
        <CategoryTab
          key={value}
          value={value}
          active={category === value}
          onCategory={onCategory}
        />
      ))}
    </div>
  );
}

/** Admin → Therapists (Figma "Therapist Management" — cards). */
export const TherapistsPage = observer(function TherapistsPage(): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Therapist | null>(null);
  const [editing, setEditing] = useState<Therapist | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [category, setCategory] = useState<CategoryFilter>('all');
  const [offset, setOffset] = useState(0);
  const [debouncedQ, setDebouncedQ] = useState('');
  const search = uiStore.search;
  const page = useTherapistsPage(pageParams(offset, debouncedQ, category));

  // Debounce the mobx search box into the server query; reset to page 1 on change.
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedQ(search.trim());
      setOffset(0);
    }, 300);
    return () => {
      clearTimeout(id);
    };
  }, [search]);

  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setOpen(true);
      searchParams.delete('new');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const openModal = useCallback(() => {
    setOpen(true);
  }, []);
  const closeModal = useCallback(() => {
    setOpen(false);
  }, []);
  const onCategory = useCallback((value: CategoryFilter) => {
    setCategory(value);
    setOffset(0);
  }, []);
  const onSelect = useCallback((therapist: Therapist) => {
    setSelected(therapist);
  }, []);
  const onProfileOpenChange = useCallback((isOpen: boolean) => {
    if (!isOpen) {
      setSelected(null);
    }
  }, []);
  const onEdit = useCallback(() => {
    setEditing(selected);
    setSelected(null);
  }, [selected]);
  const closeEdit = useCallback(() => {
    setEditing(null);
  }, []);
  const onEditOpenChange = useCallback((isOpen: boolean) => {
    if (!isOpen) {
      setEditing(null);
    }
  }, []);

  const rows = page.data?.items ?? [];
  const total = page.data?.total ?? 0;

  return (
    <div className="p-8">
      <PageHeader
        title={t('admin.therapists.title')}
        subtitle={t('admin.therapists.registered', { count: total })}
        action={
          <Button onClick={openModal}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t('admin.therapists.add')}
          </Button>
        }
      />
      <SearchBar />
      <CategoryTabs category={category} onCategory={onCategory} />

      <QueryState
        isLoading={page.isLoading}
        isError={page.isError}
        isEmpty={rows.length === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('admin.therapists.empty')}
      >
        <TherapistTable rows={rows} onSelect={onSelect} />
        <Pagination total={total} limit={PAGE_LIMIT} offset={offset} onOffsetChange={setOffset} />
      </QueryState>

      <Modal
        open={open}
        onOpenChange={setOpen}
        title={t('admin.therapists.add')}
        description={t('admin.therapists.addSubtitle')}
        closeLabel={t('common.cancel')}
      >
        <TherapistForm onDone={closeModal} />
      </Modal>

      <Modal
        open={selected !== null}
        onOpenChange={onProfileOpenChange}
        title={t('admin.therapistProfile.title')}
        closeLabel={t('common.cancel')}
      >
        {selected !== null ? <TherapistProfile therapist={selected} onEdit={onEdit} /> : <span />}
      </Modal>

      <Modal
        open={editing !== null}
        onOpenChange={onEditOpenChange}
        title={t('admin.therapists.edit')}
        description={t('admin.therapists.editSubtitle')}
        closeLabel={t('common.cancel')}
      >
        {editing !== null ? <TherapistForm onDone={closeEdit} therapist={editing} /> : <span />}
      </Modal>
    </div>
  );
});
